import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import useLocalStorage from "../hooks/useLocalStorage";

export type Language = "fr" | "en";

const DEFAULT_LANGUAGE: Language = "fr";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [storedLanguage, setStoredLanguage] = useLocalStorage<Language>(
    "language",
    DEFAULT_LANGUAGE,
  );
  const [language, setLanguage] = useState<Language>(storedLanguage);

  // Sync with localStorage when it changes
  useEffect(() => {
    setLanguage(storedLanguage);
  }, [storedLanguage]);

  // Garder <html lang> aligné sur la langue active (l'index.html généré par
  // Wasp est figé à "en" ; on le corrige au runtime pour le SEO géolinguistique).
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = language;
    }
  }, [language]);

  // Save to localStorage when language changes
  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    setStoredLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguageContext(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguageContext must be used within a LanguageProvider");
  }
  return context;
}

